import { useState } from "react";
import { parseAmount } from "../amount.ts";
import { api } from "../api.ts";
import { t, type Lang } from "../i18n.ts";
import { errorText, useMeta } from "../meta.ts";
import { hapticSelect, hapticTap } from "../telegram.ts";
import { invalidateRemote } from "../useRemote.ts";
import { AmountField } from "./AmountField.tsx";
import { RecurringSheet } from "./RecurringSheet.tsx";
import { PrimaryAction, Sheet } from "./Sheet.tsx";
import { toast } from "./Toast.tsx";

const DAYS = [1, 5, 10, 15, 20, 25];

/** Доход семьи в месяц и день зарплаты (анкета; можно поменять в «Ещё»). */
export function IncomeSheet({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  const meta = useMeta();
  const family = meta.data?.family;
  const [amount, setAmount] = useState(family?.monthlyIncome ? String(family.monthlyIncome) : "");
  const [day, setDay] = useState<number | null>(family?.incomeDay ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (busy) return;
    const value = parseAmount(amount);
    if (!value) return setError(t("fillAmount"));
    setBusy(true);
    try {
      await api.setFinance(value, day);
      hapticTap();
      toast(t("savedOk"));
      invalidateRemote();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <Sheet title={`💼 ${t("incomeTitle")}`} onClose={onClose}>
      <div className="form">
        <p className="hint">{t("incomeHint")}</p>
        <AmountField value={amount} onChange={setAmount} currency="UZS" lang={lang} label={t("incomePerMonth")} />
        <div className="field">
          <span>{t("incomeDay")}</span>
          <div className="chips">
            {DAYS.map((d) => (
              <button
                key={d}
                type="button"
                className={day === d ? "chip active" : "chip"}
                onClick={() => {
                  hapticSelect();
                  setDay(d);
                }}
              >
                {d}
              </button>
            ))}
            <button type="button" className={day === null ? "chip active" : "chip"} onClick={() => setDay(null)}>
              {t("incomeDayNone")}
            </button>
          </div>
        </div>
        {error && <p className="form-error">{error}</p>}
        <PrimaryAction label={t("save")} onClick={() => void submit()} busy={busy} />
      </div>
    </Sheet>
  );
}

/** Карточка «Настройте за минуту» на обзоре, пока семья не прошла анкету. */
export function SetupCard({ lang, hasDebts, onDebts }: { lang: Lang; hasDebts: boolean; onDebts: () => void }) {
  const meta = useMeta();
  const [sheet, setSheet] = useState<"income" | "recurring" | null>(null);
  const family = meta.data?.family;
  if (!family || family.setupDone) return null;

  const finish = async () => {
    try {
      await api.setupDone();
      invalidateRemote();
    } catch (err) {
      toast(errorText(err));
    }
  };

  const steps = [
    { key: "income", icon: "💼", title: t("setupIncome"), done: family.monthlyIncome !== null, onClick: () => setSheet("income") },
    { key: "debts", icon: "🏦", title: t("setupDebts"), done: hasDebts, onClick: onDebts },
    { key: "recurring", icon: "🔔", title: t("setupRecurring"), done: false, onClick: () => setSheet("recurring") },
  ];

  return (
    <section className="dash-card setup-card">
      <h3>✨ {t("setupTitle")}</h3>
      <p className="hint">{t("setupHint")}</p>
      <ul className="row-list">
        {steps.map((s) => (
          <li key={s.key}>
            <button type="button" className="row row-btn" onClick={s.onClick}>
              <span className="row-icon" aria-hidden="true">
                {s.done ? "✅" : s.icon}
              </span>
              <div className="row-body">
                <div className="row-title">{s.title}</div>
              </div>
              <span className="chevron" aria-hidden="true">
                ›
              </span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="btn btn-block" onClick={() => void finish()}>
        {t("setupDone")}
      </button>
      {sheet === "income" && <IncomeSheet lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "recurring" && <RecurringSheet lang={lang} onClose={() => setSheet(null)} />}
    </section>
  );
}
